import React from 'react';

import AssignmentChip from '../chips/AssignmentChip';
import AssignmentPlaceholderChip from '../chips/AssignmentPlaceholderChip';
import TaskItemAssignmentPopupContent from '../chips/TaskItemAssignmentPopupContent';
import { useAssignmentTaskItemChipCallbacks } from '../hooks/useAssignmentTaskItemChipCallbacks';
import { replaceAssignmentList } from '../../../../common/tiptap/extensions/taskList/taskListItemAssignmentUtils';
import type { ElementAssignmentChipDescriptor, ElementAssignmentPlaceholderChipDescriptor } from '../elementChipsTypes';
import type { TaskItemAssignment } from '../../../../common/tiptap/extensions/taskList/taskListItemChipTypes';
import type { UpdateTaskItemAttrs } from '../taskItemChipsUtils';
import { ChipBaseProps } from '../../../../common/@roma/src/components/base/chip/ChipBase';

type TaskItemAssignmentChipProps = {
    chip: ElementAssignmentChipDescriptor | ElementAssignmentPlaceholderChipDescriptor;
    size: ChipBaseProps['size'];
    assignments: TaskItemAssignment[];
    updateAttributes: UpdateTaskItemAttrs;
    canModify: boolean;
};

/**
 * Assignment (or placeholder) chip for a Tiptap task item. The picker's writes
 * go through the owning node's `updateAttributes` (like the other task-item
 * chips) rather than the element-id popup the element chip uses: picking a
 * user replaces the current assignee (a task has a single assignee),
 * deleting/clearing empties the assignees and turns off the slot.
 */
const TaskItemAssignmentChip: React.FC<TaskItemAssignmentChipProps> = ({
    chip,
    size,
    assignments,
    updateAttributes,
    canModify,
}) => {
    const { onRemove } = useAssignmentTaskItemChipCallbacks({
        updateAttributes,
        canModifyAssignments: canModify,
    });

    const renderPopupContent = ({ closePopup }: { closePopup: () => void }) => (
        <TaskItemAssignmentPopupContent
            assignments={assignments}
            onAssign={({ id, name }) =>
                updateAttributes({
                    assignments: replaceAssignmentList(assignments, { assigneeId: id, assigneeName: name }),
                    showAssignments: true,
                })
            }
            onRemoveAll={onRemove}
            closePopup={closePopup}
        />
    );

    return chip.type === 'assignment' ? (
        <AssignmentChip
            chip={chip}
            size={size}
            canModify={canModify}
            onRemove={onRemove}
            renderPopupContent={renderPopupContent}
        />
    ) : (
        <AssignmentPlaceholderChip
            chip={chip}
            size={size}
            canModify={canModify}
            onRemove={onRemove}
            renderPopupContent={renderPopupContent}
        />
    );
};

export default TaskItemAssignmentChip;
